import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Divider,
  Group,
  List,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { SelectPatientModal, type SelectPatientRow } from '@/components/ui'
import { USDateInput } from '@/components/ui/USDateInput'
import { FIELD_CLASSES, MODAL_CONTENT } from '@/components/ui/formModal.tokens'
import { cn } from '@/utils/cn'
import { formatCalendarDay, formatWallClockTime } from './zocdocFormat'
import { useClient, type OrpcClient } from '@/hooks/useClient'
import { DANGER_BTN, HINT, ID_CHIP, SECONDARY_BTN, TABLE_HEAD, TABLE_ROW } from './tokens'

type BookResult = Awaited<ReturnType<OrpcClient['zocdocCreateAppointment']>>
type VisitReasonsResult = Awaited<ReturnType<OrpcClient['zocdocVisitReasons']>>
type PatientType = Parameters<OrpcClient['zocdocCreateAppointment']>[0]['patientType']

/**
 * `patient_type` is Zocdoc's "has this person been to the practice before", and on this screen it
 * follows from which patient source is in use rather than being a free choice: a chart we already
 * hold books as `existing`, someone typed in from scratch books as `new`. Shown disabled rather
 * than hidden, because it is still part of what gets sent.
 */
const PATIENT_TYPE: Record<'chart' | 'new', PatientType> = {
  chart: 'existing',
  new: 'new',
}

/** Exactly the fields `POST /v1/appointments` documents as required, plus its optional line 2. */
type NewPatientForm = {
  firstName: string
  lastName: string
  dateOfBirth: string
  sexAtBirth: 'male' | 'female' | null
  phone: string
  email: string
  address1: string
  address2: string
  city: string
  state: string
  zipCode: string
}

const emptyNewPatient = (): NewPatientForm => ({
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  sexAtBirth: null,
  phone: '',
  email: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
})

/**
 * Enough to send. Zocdoc's own rules — the phone's shape, the two-letter state, the five-digit ZIP
 * — are **not** re-checked here: middleware owns them, and duplicating them in the browser is how
 * the two drift apart. This only gates the button on nothing being blank.
 */
const newPatientComplete = (form: NewPatientForm) =>
  Boolean(
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.dateOfBirth.trim() &&
    form.sexAtBirth &&
    form.phone.trim() &&
    form.email.trim() &&
    form.address1.trim() &&
    form.city.trim() &&
    form.state.trim() &&
    form.zipCode.trim(),
  )

/** The clicked slot, flattened out of the calendar so this component needs nothing else. */
export type SlotToBook = {
  npi: string
  /** `YYYY-MM-DD` and `HH:mm`, local to `timeZone` — the slot exactly as Zocdoc stores it. */
  day: string
  time: string
  timeZone?: string
  /** Bare `lo_…`. */
  locationId?: string
  locationName?: string
  locationVirtual?: boolean
  /** Zocdoc's own patient-type restriction on the slot. Displayed, not chosen — see below. */
  patientType?: PatientType
  allowedVisitReasonIds?: string[]
  excludedVisitReasonIds?: string[]
}

/**
 * The new-patient section: exactly the fields `POST /v1/appointments` documents as required for
 * `data.patient`, plus its optional second address line.
 *
 * Nothing optional is invented — no insurance block, no gender-identity array — because a booking
 * form that asks for more than the API needs is a form people abandon. Zocdoc's own rules (the
 * ten-digit phone, the two-letter state, the five-digit ZIP) are checked by **middleware** on
 * Validate rather than re-implemented here: one owner for those rules means the browser and the
 * server cannot disagree about what Zocdoc accepts.
 */
function NewPatientFields({
  form,
  onChange,
}: {
  form: NewPatientForm
  onChange: (next: NewPatientForm) => void
}) {
  const set = <K extends keyof NewPatientForm>(key: K, value: NewPatientForm[K]) =>
    onChange({ ...form, [key]: value })

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[#ECEEF1] bg-[#FBFCFD] p-3.5">
      <div>
        <h3 className="behuman-v2-mulish m-0 !text-[11px] !font-bold !tracking-[0.06em] !text-[#6B7280] uppercase">
          New patient
        </h3>
        <p className={cn([HINT, 'mt-1 mb-0'])}>
          Every field here is one Zocdoc requires. Nothing is written to beHuman — the chart is
          created when Zocdoc announces the booking, like any other.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <TextInput
          classNames={FIELD_CLASSES}
          label="First name"
          value={form.firstName}
          onChange={(event) => set('firstName', event.currentTarget.value)}
          required
        />
        <TextInput
          classNames={FIELD_CLASSES}
          label="Last name"
          value={form.lastName}
          onChange={(event) => set('lastName', event.currentTarget.value)}
          required
        />
        <USDateInput
          classNames={FIELD_CLASSES}
          label="Date of birth"
          value={form.dateOfBirth}
          onChange={(value) => set('dateOfBirth', value ?? '')}
          required
        />
        <Select
          classNames={FIELD_CLASSES}
          label="Sex at birth"
          // Zocdoc's field, and Zocdoc accepts only these two — so the form cannot offer
          // `other`/`unknown` and then have the booking refused for it.
          description="Zocdoc accepts only male or female"
          placeholder="Pick one"
          data={[
            { value: 'male', label: 'Male' },
            { value: 'female', label: 'Female' },
          ]}
          value={form.sexAtBirth}
          onChange={(value) =>
            set('sexAtBirth', value === 'male' || value === 'female' ? value : null)
          }
          required
        />
        <TextInput
          classNames={FIELD_CLASSES}
          label="Phone"
          description="US number — middleware strips it to Zocdoc's ten digits"
          value={form.phone}
          onChange={(event) => set('phone', event.currentTarget.value)}
          required
        />
        <TextInput
          classNames={FIELD_CLASSES}
          label="Email"
          type="email"
          value={form.email}
          onChange={(event) => set('email', event.currentTarget.value)}
          required
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
        <TextInput
          classNames={FIELD_CLASSES}
          className="sm:col-span-4"
          label="Street address"
          value={form.address1}
          onChange={(event) => set('address1', event.currentTarget.value)}
          required
        />
        <TextInput
          classNames={FIELD_CLASSES}
          className="sm:col-span-2"
          label="Apt, suite"
          placeholder="Optional"
          value={form.address2}
          onChange={(event) => set('address2', event.currentTarget.value)}
        />
        <TextInput
          classNames={FIELD_CLASSES}
          className="sm:col-span-3"
          label="City"
          value={form.city}
          onChange={(event) => set('city', event.currentTarget.value)}
          required
        />
        <TextInput
          classNames={FIELD_CLASSES}
          className="sm:col-span-1"
          label="State"
          placeholder="IN"
          maxLength={2}
          value={form.state}
          onChange={(event) => set('state', event.currentTarget.value.toUpperCase())}
          required
        />
        <TextInput
          classNames={FIELD_CLASSES}
          className="sm:col-span-2"
          label="ZIP"
          placeholder="46204"
          maxLength={5}
          value={form.zipCode}
          onChange={(event) => set('zipCode', event.currentTarget.value)}
          required
        />
      </div>
    </div>
  )
}

/**
 * Books one clicked slot for a beHuman patient — `POST /v2/zocdoc/appointments`.
 *
 * Two-step by design, matching the endpoint: **Validate** runs the dry run (resolving the provider,
 * the location's time zone and every Zocdoc patient rule without sending anything) and **Book on
 * Zocdoc** commits. The booking call is not idempotent and Zocdoc has no idempotency key, so a
 * single mis-click must not be able to create a real appointment — and the same reason is why the
 * Book button re-locks the moment any field changes.
 *
 * Nothing lands in FHIR from here: Zocdoc announces the booking over the webhook and the inbound
 * ingest path creates the chart side.
 */
/** One row of the patient block, with what the chart holds and what Zocdoc will receive. */
type PayloadRow = {
  label: string
  /** What the chart holds. */
  chart: string
  /** What goes on the wire once middleware has validated it, when we know it. */
  sent?: string
  /** Zocdoc rejects the booking without it. */
  required: boolean
}

/**
 * Everything about the chosen patient that this booking sends to Zocdoc, shown **before** anything
 * is sent.
 *
 * Two columns, not one, because they genuinely differ: middleware strips the phone to ten digits
 * and upper-cases the state, and a required field the chart is missing shows as a gap here rather
 * than as a Zocdoc rejection later. Before Validate runs there is no wire column yet, so the chart
 * values stand alone and required gaps are already flagged.
 */
/**
 * The left column's values, from whichever source is in use. Both sources are flattened to the
 * same shape so there is one table rather than two that can disagree about what gets sent.
 */
type PayloadSource = {
  label: string
  firstName: string
  lastName: string
  birthdate: string
  gender: string
  phone: string
  email: string
  address: string
  city: string
  state: string
  zip: string
  /** Absent for a patient with no chart, which is why `developer_patient_id` is not sent. */
  id?: string
}

const sourceFromChart = (row: SelectPatientRow): PayloadSource => ({
  label: 'In the chart',
  firstName: row.firstName,
  lastName: row.lastName,
  birthdate: row.birthdate,
  gender: row.gender,
  phone: row.phone,
  email: row.email,
  address: row.address,
  city: row.city,
  state: row.state,
  zip: row.zip,
  id: row.id,
})

const sourceFromForm = (form: NewPatientForm): PayloadSource => ({
  label: 'Typed in',
  firstName: form.firstName,
  lastName: form.lastName,
  birthdate: form.dateOfBirth,
  gender: form.sexAtBirth ?? '',
  phone: form.phone,
  email: form.email,
  address: [form.address1, form.address2].filter(Boolean).join(', '),
  city: form.city,
  state: form.state,
  zip: form.zipCode,
})

function PatientPayloadTable({
  patient,
  sent,
}: {
  patient: PayloadSource
  sent?: NonNullable<NonNullable<BookResult['payload']>['patient']>
}) {
  const rows: PayloadRow[] = [
    { label: 'First name', chart: patient.firstName, sent: sent?.firstName, required: true },
    { label: 'Last name', chart: patient.lastName, sent: sent?.lastName, required: true },
    { label: 'Date of birth', chart: patient.birthdate, sent: sent?.dateOfBirth, required: true },
    // Zocdoc's field is sex at birth and accepts only male/female — see the handler, which refuses
    // rather than coercing `other`/`unknown`.
    { label: 'Sex at birth', chart: patient.gender, sent: sent?.sexAtBirth, required: true },
    { label: 'Phone', chart: patient.phone, sent: sent?.phoneNumber, required: true },
    { label: 'Email', chart: patient.email, sent: sent?.emailAddress, required: true },
    { label: 'Address', chart: patient.address, sent: sent?.address1, required: true },
    { label: 'City', chart: patient.city, sent: sent?.city, required: true },
    { label: 'State', chart: patient.state, sent: sent?.state, required: true },
    { label: 'ZIP', chart: patient.zip, sent: sent?.zipCode, required: true },
    // Only meaningful when a chart is the source; a typed-in patient has no id to send, which is
    // itself worth showing rather than leaving the reader to wonder.
    {
      label: 'beHuman patient id',
      chart: patient.id ?? 'none — no chart yet',
      sent: sent?.developerPatientId,
      required: false,
    },
  ]

  return (
    <Stack gap={4}>
      <Text className="!text-[11px] !font-bold !tracking-[0.06em] !text-[#6B7280] uppercase">
        Sent to Zocdoc
      </Text>
      <Table verticalSpacing={6} fz="xs">
        <Table.Thead>
          <Table.Tr className={TABLE_ROW}>
            <Table.Th className={cn([TABLE_HEAD, 'w-[150px]'])}>Field</Table.Th>
            <Table.Th className={TABLE_HEAD}>{patient.label}</Table.Th>
            <Table.Th className={TABLE_HEAD}>On the wire</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => {
            const missing = row.required && !row.chart
            return (
              <Table.Tr key={row.label} className={TABLE_ROW}>
                <Table.Td className="font-bold text-[#6B7280]">{row.label}</Table.Td>
                <Table.Td className={missing ? 'font-bold text-[#DC2626]' : undefined}>
                  {row.chart || (missing ? 'missing — Zocdoc requires this' : '—')}
                </Table.Td>
                <Table.Td>
                  {row.sent ? (
                    // Highlighted only when it differs, so normalisation is visible without
                    // turning the whole column into noise.
                    <span
                      className={cn([
                        ID_CHIP,
                        row.sent !== row.chart && 'bg-[#FEF3C7] !text-[#B45309]',
                      ])}
                    >
                      {row.sent}
                    </span>
                  ) : (
                    <span className={HINT}>—</span>
                  )}
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
      {!sent && (
        <Text size="xs" className="!text-[#6B7280]">
          Validate to see the exact values Zocdoc will receive.
        </Text>
      )}
    </Stack>
  )
}

export function BookSlotModal({ slot, onClose }: { slot: SlotToBook | null; onClose: () => void }) {
  const client = useClient()
  const [patient, setPatient] = useState<SelectPatientRow | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  /** `null` = no new-patient section; an object = the section is open and holds the draft. */
  const [newPatient, setNewPatient] = useState<NewPatientForm | null>(null)
  const [visitReasonId, setVisitReasonId] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [reasons, setReasons] = useState<VisitReasonsResult | null>(null)
  const [reasonsLoading, setReasonsLoading] = useState(false)
  // Which button is in flight. Explicit rather than inferred from `validated`, which flips
  // underneath a request and would spin the wrong button.
  const [pending, setPending] = useState<'validate' | 'book' | null>(null)
  const [result, setResult] = useState<BookResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  // A fresh slot is a fresh booking: carrying the previous patient or reason over would be the
  // easiest possible way to book the wrong person.
  useEffect(() => {
    setPatient(null)
    setNewPatient(null)
    setVisitReasonId(null)
    setNotes('')
    setResult(null)
    setError(null)
  }, [slot])

  /**
   * The provider's own supported reasons, labelled by middleware — see
   * `GET /v2/zocdoc/visit-reasons`. Narrowed to the slot's `allowedVisitReasonIds` when the slot
   * restricts them, because a reason outside that set is what Zocdoc rejects at booking time.
   */
  useEffect(() => {
    if (!slot?.npi) return
    let cancelled = false
    setReasonsLoading(true)
    setReasons(null)
    client
      .zocdocVisitReasons({
        npi: slot.npi,
        ...(slot.allowedVisitReasonIds?.length && { onlyIds: slot.allowedVisitReasonIds }),
      })
      .then((loaded) => {
        if (!cancelled) setReasons(loaded)
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Could not load visit reasons')
        }
      })
      .finally(() => {
        if (!cancelled) setReasonsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client, slot])

  const reasonOptions = useMemo(() => {
    const excluded = new Set(slot?.excludedVisitReasonIds ?? [])
    return (reasons?.reasons ?? [])
      .filter((reason) => !excluded.has(reason.id))
      .map((reason) => ({
        value: reason.id,
        // The bare id when Zocdoc gave no name: still bookable, and honest about what it is.
        label: reason.name ?? reason.id,
      }))
  }, [reasons, slot])

  const unrestricted = !slot?.allowedVisitReasonIds?.length

  const submit = useCallback(
    async (dryRun: boolean) => {
      if (!slot || !visitReasonId || !slot.locationId) return
      // Exactly one source, which the UI enforces by only ever having one of the two open.
      // `sexAtBirth` is narrowed rather than asserted: the contract's enum has no null member, so
      // an un-picked value would be a 400 from middleware instead of a message in the form.
      const source =
        newPatient && newPatient.sexAtBirth
          ? { newPatient: { ...newPatient, sexAtBirth: newPatient.sexAtBirth } }
          : !newPatient && patient
            ? { patientId: patient.id }
            : null
      if (!source) return

      setPending(dryRun ? 'validate' : 'book')
      setError(null)
      try {
        setResult(
          await client.zocdocCreateAppointment({
            npi: slot.npi,
            ...source,
            locationId: slot.locationId,
            day: slot.day,
            time: slot.time,
            ...(slot.timeZone && { timeZone: slot.timeZone }),
            visitReasonId,
            patientType: newPatient ? PATIENT_TYPE.new : PATIENT_TYPE.chart,
            ...(notes.trim() && { notes: notes.trim() }),
            dryRun,
          }),
        )
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Could not reach middleware')
      } finally {
        setPending(null)
      }
    },
    [client, slot, patient, newPatient, visitReasonId, notes],
  )

  /**
   * Only a clean dry run unlocks the real booking.
   *
   * Every field's `onChange` clears `result`, so any edit after validating re-locks the Book button
   * — the thing that was validated and the thing that would be sent are always the same thing. The
   * booking call cannot be retried safely, so this gate is the whole safety story.
   */
  const validated = result?.dryRun === true && result.problems.length === 0 && !result.error
  // Derived once so the table needs no non-null assertion: the two sources are exclusive, and
  // this is the single place that decides which one is in play.
  const payloadSource = newPatient
    ? sourceFromForm(newPatient)
    : patient
      ? sourceFromChart(patient)
      : null

  const patientReady = newPatient ? newPatientComplete(newPatient) : Boolean(patient)
  const ready = Boolean(patientReady && visitReasonId && slot?.locationId)
  const bookBlockedBecause = !ready
    ? newPatient && !newPatientComplete(newPatient)
      ? 'Fill every new-patient field Zocdoc requires'
      : 'Choose a patient and a visit reason first'
    : !result
      ? 'Validate first — the booking cannot be undone by a second call'
      : !validated && !result.booked
        ? 'Fix what Validate reported, then validate again'
        : null

  return (
    <>
      <Modal
        opened={slot !== null}
        onClose={onClose}
        size="lg"
        radius={18}
        classNames={{
          content: MODAL_CONTENT,
          title: '!text-[17px] !font-extrabold !tracking-[-0.3px]',
        }}
        title={slot ? `Book ${formatCalendarDay(slot.day)} ${formatWallClockTime(slot.time)}` : ''}
      >
        {slot && (
          <Stack gap="sm">
            <Group gap="xs">
              {/* A name reads as a name; a bare id gets monospace rather than being shouted in
                  uppercase, which is what made `LO_5S71AOOKO…` unreadable. */}
              {slot.locationName ? (
                <span
                  className={cn([
                    'behuman-v2-mulish inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] uppercase',
                    slot.locationVirtual
                      ? 'bg-[#CCFBF1] text-[#0F766E]'
                      : 'bg-[#DBEAFE] text-[#1D4ED8]',
                  ])}
                >
                  {slot.locationName}
                </span>
              ) : (
                <span className={ID_CHIP}>{slot.locationId ?? 'unknown location'}</span>
              )}
              {slot.timeZone && (
                <Text size="xs" className="!text-[#6B7280]">
                  {slot.timeZone}
                </Text>
              )}
              <Text size="xs" className="!text-[#6B7280]">
                NPI {slot.npi}
              </Text>
            </Group>

            {!slot.locationId && (
              <Alert color="red" title="This slot cannot be booked">
                Zocdoc returned no location id for it, and a booking needs one.
              </Alert>
            )}

            <Divider />

            <Group justify="space-between" align="center">
              <Text size="sm" fw={600}>
                {newPatient
                  ? 'New patient'
                  : patient
                    ? `${patient.firstName} ${patient.lastName}`
                    : 'No patient chosen'}
              </Text>
              {/* The two sources are mutually exclusive, so choosing one clears the other rather
                  than leaving both on screen for the reader to reconcile. */}
              <Group gap={8}>
                <Button
                  {...SECONDARY_BTN}
                  onClick={() => {
                    setNewPatient(null)
                    setResult(null)
                    setPickerOpen(true)
                  }}
                >
                  {patient ? 'Change patient' : 'Choose patient'}
                </Button>
                <Button
                  {...SECONDARY_BTN}
                  aria-expanded={newPatient !== null}
                  onClick={() => {
                    setNewPatient((current) => (current ? null : emptyNewPatient()))
                    setPatient(null)
                    setResult(null)
                  }}
                >
                  {newPatient ? 'Cancel new patient' : 'New patient'}
                </Button>
              </Group>
            </Group>

            {newPatient && (
              <NewPatientFields
                form={newPatient}
                onChange={(next) => {
                  setNewPatient(next)
                  setResult(null)
                }}
              />
            )}

            {payloadSource && (
              <PatientPayloadTable patient={payloadSource} sent={result?.payload?.patient} />
            )}

            <Select
              classNames={FIELD_CLASSES}
              label="Reason for visit"
              description={
                reasonsLoading
                  ? 'Reading the provider’s visit reasons from Zocdoc…'
                  : unrestricted
                    ? `${reasonOptions.length} reason${reasonOptions.length === 1 ? '' : 's'} this provider supports`
                    : `${reasonOptions.length} of the provider’s ${reasons?.supportedCount ?? 0} reasons are accepted by this slot`
              }
              placeholder={reasonsLoading ? 'Loading…' : 'Pick a visit reason'}
              data={reasonOptions}
              value={visitReasonId}
              onChange={(value) => {
                setVisitReasonId(value)
                setResult(null)
              }}
              disabled={reasonsLoading || reasonOptions.length === 0}
              rightSection={reasonsLoading ? <Loader size="xs" /> : undefined}
              searchable
              nothingFoundMessage="No match"
              limit={100}
            />

            {reasons?.error && (
              <Alert color="red" title="Could not read this provider’s visit reasons">
                {reasons.error}
              </Alert>
            )}

            {reasons?.labelWarning && (
              <Alert color="yellow" title="Visit reasons are shown by id">
                {reasons.labelWarning}
              </Alert>
            )}

            {reasons && !reasons.error && reasons.reasons.length === 0 && (
              <Alert color="orange">
                Zocdoc lists no visit reason this slot accepts, so it cannot be booked.
              </Alert>
            )}

            <Select
              classNames={FIELD_CLASSES}
              label="Patient type"
              description={
                newPatient
                  ? 'Follows the new-patient form: someone with no chart has not been to the practice through us'
                  : 'Follows the picker: a chart beHuman already holds books as existing'
              }
              data={[
                { value: 'existing', label: 'Existing patient' },
                { value: 'new', label: 'New patient' },
              ]}
              value={newPatient ? PATIENT_TYPE.new : PATIENT_TYPE.chart}
              disabled
              allowDeselect={false}
            />

            <Textarea
              classNames={FIELD_CLASSES}
              label="Notes"
              description="Optional. Zocdoc allows 100 characters."
              value={notes}
              onChange={(event) => {
                setNotes(event.currentTarget.value)
                setResult(null)
              }}
              maxLength={100}
              autosize
              minRows={2}
            />

            {error && <Alert color="red">{error}</Alert>}

            {result?.missingEnv?.length ? (
              <Alert color="red" title="Zocdoc is not configured">
                Unset: {result.missingEnv.join(', ')}
              </Alert>
            ) : null}

            {result && result.problems.length > 0 && (
              <Alert color="orange" title="This booking cannot be sent yet">
                <List size="sm" spacing={2}>
                  {result.problems.map((problem) => (
                    <List.Item key={problem}>{problem}</List.Item>
                  ))}
                </List>
              </Alert>
            )}

            {result?.error && (
              <Alert color="red" title="Zocdoc did not accept the booking">
                <Stack gap={2}>
                  <Text size="sm">{result.error}</Text>
                  {result.requestId && (
                    <Text size="xs" className="!text-[#6B7280]">
                      Zocdoc request id <span className={ID_CHIP}>{result.requestId}</span> — quote
                      it to their support.
                    </Text>
                  )}
                </Stack>
              </Alert>
            )}

            {result?.payload && (
              <Alert
                color={result.booked ? 'green' : validated ? 'blue' : 'gray'}
                title={
                  result.booked
                    ? 'Booked on Zocdoc'
                    : validated
                      ? 'Valid — nothing sent yet'
                      : 'Payload'
                }
              >
                <Stack gap={2}>
                  <Text size="sm">
                    {result.payload.startTime} · {result.payload.patientName} ·{' '}
                    {result.payload.patientType}
                  </Text>
                  <Text size="xs" className="!text-[#6B7280]">
                    <span className={ID_CHIP}>{result.payload.providerLocationId}</span>{' '}
                    <span className={ID_CHIP}>{result.payload.visitReasonId}</span>
                  </Text>
                  {result.booked && (
                    <Text size="xs" className="!text-[#6B7280]">
                      {result.appointmentId ? (
                        <>
                          Zocdoc appointment <span className={ID_CHIP}>{result.appointmentId}</span>
                          .{' '}
                        </>
                      ) : (
                        // Zocdoc's documented 200 omits the id, so its absence is expected and
                        // says nothing about whether the booking exists.
                        <>Zocdoc returned no appointment id. </>
                      )}
                      It lands in <span className={ID_CHIP}>pending_booking</span> and still needs
                      confirming; the FHIR appointment is created by the inbound sync, not here.
                    </Text>
                  )}
                </Stack>
              </Alert>
            )}

            <Group justify="flex-end">
              <Button {...SECONDARY_BTN} onClick={onClose}>
                Close
              </Button>
              <Button
                {...SECONDARY_BTN}
                loading={pending === 'validate'}
                disabled={!ready || pending !== null || result?.booked}
                onClick={() => void submit(true)}
              >
                Validate
              </Button>
              <Button
                {...DANGER_BTN}
                loading={pending === 'book'}
                disabled={!validated || pending !== null || result?.booked}
                onClick={() => void submit(false)}
              >
                Book on Zocdoc
              </Button>
            </Group>

            {bookBlockedBecause && !result?.booked && (
              <Text size="xs" className="!text-[#6B7280]" ta="right">
                {bookBlockedBecause}
              </Text>
            )}

            <Text size="xs" className="!text-[#6B7280]">
              Booking creates a real appointment on whichever Zocdoc environment these credentials
              belong to, and it cannot be retried safely — validate first, then book once.
            </Text>
          </Stack>
        )}
      </Modal>

      {/* A sibling rather than a child modal: nesting two Mantine modals fights over focus, and
          the picker only ever needs to sit above this one. */}
      <SelectPatientModal
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(chosen) => {
          setPatient(chosen)
          setResult(null)
          setPickerOpen(false)
        }}
      />
    </>
  )
}
